import { useHistory, useParams } from 'react-router-dom';

import {
    setCallMethod,
    setAutoAnswerWithIpPhone,
    setMakeCallsEnabled,
} from 'flow/actions/app-actions';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import Divider from 'flow/components/ui-components/Divider';
import InfoBox, { InfoBoxType } from 'flow/components/ui-components/InfoBox';
import RadioButton from 'flow/components/ui-components/RadioButton';
import Toggle from 'flow/components/ui-components/Toggle';
import SoftphoneOutgoingCallsSkeleton from 'flow/components/loaders/SoftphoneOutgoingCallsSkeleton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    UserAppSettingOption,
    getCallMethodFromSetting,
    getSettingFromCallMethod,
    isSettingAppliedEverywhere,
    useOutgoingCallSettings,
    useOutgoingCallsEnabled,
    useUpdateOutgoingCallSetting,
} from 'flow/hooks/api/useSoftphoneCallSettings';
import { useUserCallRights } from 'flow/hooks/api/useUserRightsSetting';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import {
    getCallMethod,
    getAutoAnswerWithIpPhone,
    hasMobileExtension,
} from 'flow/selectors/selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { CallMethodType, isRegisteredSIP } from 'flow/utils/call-utils';
import { generatePath } from 'flow/utils/router-utils';
import useExtension from 'flow/hooks/useExtension';
import { Capabilities } from 'flow/utils/capability-utils';

const SoftphoneOutgoingCalls = () => {
    const { i18next, phoneEnabled } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { extension } = useExtension(extensionKey);

    const hasCapabilitySoftphone = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SOFTPHONE),
    );

    const { canConfigureOutgoingCalls } = useUserCallRights();
    const isReadOnly =
        !hasCapabilitySoftphone || !phoneEnabled || !canConfigureOutgoingCalls;

    const callMethod = useAppSelector(getCallMethod);
    const autoAnswerWithIpPhone = useAppSelector(getAutoAnswerWithIpPhone);
    const hasMex = useAppSelector(hasMobileExtension);
    const hasRegisteredTerminal = isRegisteredSIP(extension);
    const { data: outgoingSettings, isLoading } = useOutgoingCallSettings();
    const { mutate: updateOutgoingCallSetting } =
        useUpdateOutgoingCallSetting();

    const settingOption = outgoingSettings?.value;
    const useSettingEverywhere = isSettingAppliedEverywhere(settingOption);

    const effectiveCallMethod = useSettingEverywhere
        ? (getCallMethodFromSetting(settingOption) ?? callMethod)
        : callMethod;

    const effectiveMakeCallsEnabled = useOutgoingCallsEnabled();

    const handleMakeCallsToggle = () => {
        const newValue = !effectiveMakeCallsEnabled;

        // Neither branch touches the softphone registration, so incoming calls keep working
        // (AC-1902).
        if (useSettingEverywhere) {
            // useSyncOutgoingCallSetting carries the new value to this client from the optimistic
            // update, and carries the old one back if the server rejects it.
            updateOutgoingCallSetting(
                newValue
                    ? getSettingFromCallMethod(effectiveCallMethod)
                    : UserAppSettingOption.OFF,
            );
        } else {
            dispatch(setMakeCallsEnabled(newValue));
        }
    };

    const handleUseSettingEverywhereChange = (useEverywhere: boolean) => {
        const currentSetting = effectiveMakeCallsEnabled
            ? getSettingFromCallMethod(effectiveCallMethod)
            : UserAppSettingOption.OFF;
        const newSetting = useEverywhere
            ? currentSetting
            : UserAppSettingOption.PER_CLIENT;
        updateOutgoingCallSetting(newSetting);
    };

    const callMethodTypes = [
        CallMethodType.ALWAYS_ASK,
        CallMethodType.LOCAL,
        ...(hasMex ? [CallMethodType.MOBILE] : []),
        CallMethodType.IP_PHONE,
    ];

    return (
        <Page
            header="settings.softphone.outgoing_calls"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone'], {
                        extensionKey,
                    }),
                )
            }
        >
            {isLoading ? (
                <SoftphoneOutgoingCallsSkeleton />
            ) : (
                <div
                    className={
                        isReadOnly ? 'pointer-events-none opacity-50' : ''
                    }
                >
                    {isReadOnly && (
                        <InfoBox type={InfoBoxType.Info} className="mb-2">
                            {i18next.t('settings.softphone.no_permission')}
                        </InfoBox>
                    )}

                    <SettingRow
                        cta={
                            <Toggle
                                value={effectiveMakeCallsEnabled}
                                onChange={handleMakeCallsToggle}
                                disabled={isReadOnly}
                                text={i18next.t(
                                    'settings.softphone.make_calls',
                                )}
                            />
                        }
                    >
                        {i18next.t('settings.softphone.make_calls')}
                    </SettingRow>

                    <Divider className="my-2" />

                    {hasCapabilitySoftphone && (
                        <div
                            className={
                                !effectiveMakeCallsEnabled
                                    ? 'pointer-events-none opacity-50'
                                    : ''
                            }
                        >
                            <SettingSection>
                                {i18next.t('call.method')}
                            </SettingSection>

                            {callMethodTypes.map((option) => {
                                const isSelected =
                                    effectiveCallMethod.is(option);
                                const showIpPhoneWarning =
                                    option === CallMethodType.IP_PHONE &&
                                    isSelected &&
                                    !hasRegisteredTerminal;

                                return (
                                    <div
                                        key={`callMethod-option-${option.value}`}
                                    >
                                        <RadioButton
                                            name={option.key}
                                            checked={isSelected}
                                            disabled={isReadOnly}
                                            onChange={() => {
                                                dispatch(setCallMethod(option));
                                                if (useSettingEverywhere) {
                                                    updateOutgoingCallSetting(
                                                        getSettingFromCallMethod(
                                                            option,
                                                        ),
                                                    );
                                                }
                                            }}
                                        >
                                            <>
                                                {i18next.t(
                                                    `settings.callmethod.${option.value}`,
                                                )}
                                                {showIpPhoneWarning ? (
                                                    <div className="text-red">
                                                        {i18next.t(
                                                            'settings.callmethod.ipPhone.missing',
                                                        )}
                                                    </div>
                                                ) : null}
                                            </>
                                        </RadioButton>
                                    </div>
                                );
                            })}

                            {effectiveCallMethod.is(CallMethodType.IP_PHONE) ? (
                                <>
                                    <Divider className="my-4" />
                                    <SettingRow
                                        cta={
                                            <Toggle
                                                value={autoAnswerWithIpPhone}
                                                onChange={() =>
                                                    dispatch(
                                                        setAutoAnswerWithIpPhone(
                                                            !autoAnswerWithIpPhone,
                                                        ),
                                                    )
                                                }
                                                text={i18next.t(
                                                    'call.method.auto.answer',
                                                )}
                                            />
                                        }
                                        information={i18next.t(
                                            'call.method.hint.auto_answer',
                                        )}
                                    >
                                        {i18next.t('call.method.auto.answer')}
                                    </SettingRow>
                                </>
                            ) : null}

                            <Divider className="my-2" />
                        </div>
                    )}

                    <SettingSection>
                        {i18next.t('settings.softphone.options')}
                    </SettingSection>

                    <RadioButton
                        name="outgoing-calls-option"
                        checked={!useSettingEverywhere}
                        disabled={isReadOnly}
                        onChange={() => handleUseSettingEverywhereChange(false)}
                    >
                        {i18next.t('settings.softphone.configure_per_client')}
                    </RadioButton>

                    <RadioButton
                        name="outgoing-calls-option"
                        checked={useSettingEverywhere}
                        disabled={isReadOnly}
                        onChange={() => handleUseSettingEverywhereChange(true)}
                    >
                        {i18next.t('settings.softphone.use_setting_everywhere')}
                    </RadioButton>
                </div>
            )}
        </Page>
    );
};

export default SoftphoneOutgoingCalls;
